/**
 * The brief "Saved" beside a setting once a change is kept, for screens that
 * have no Save button and so need some other sign the tap took.
 *
 * Used by: every profile screen whose changes are kept on a tap.
 *
 * @module useSavedTicks
 */

import React from 'react';
import { SAVED_TICK_MS } from '@/config/tunables/garden';

/**
 * A "Saved" that shows for a moment after a change is kept.
 *
 * `flash(key)` lights the one for that key; several can be on at once.
 */
export function useSavedTicks(): { saved: (key: string) => boolean; flash: (key: string) => void } {
  const [on, setOn] = React.useState<Record<string, number>>({});
  const timers = React.useRef<Record<string, number>>({});
  React.useEffect(() => () => Object.values(timers.current).forEach((id) => window.clearTimeout(id)), []);
  /** Lights the "Saved" for one key, restarting its moment if it is already on. */
  const flash = React.useCallback((key: string) => {
    window.clearTimeout(timers.current[key]);
    setOn((was) => ({ ...was, [key]: Date.now() }));
    timers.current[key] = window.setTimeout(() => {
      setOn((was) => {
        const next = { ...was };
        delete next[key];
        return next;
      });
    }, SAVED_TICK_MS);
  }, []);
  /** Whether the "Saved" for a key is showing. */
  const saved = React.useCallback((key: string) => key in on, [on]);
  return { saved, flash };
}
